import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { WorkerProfileModal } from '../../components/shared/WorkerProfileModal';
import WorkerAvatar from '../../components/shared/WorkerAvatar';
import BestMatchCard from '../../components/manager/BestMatchCard';
import { 
  CheckCircle2, XCircle, Star, ShieldCheck, MapPin, Clock, Banknote,
  Users, Loader2, MessageSquare, Eye, AlertTriangle, UserX, Info, Phone, RefreshCw, CalendarRange
} from 'lucide-react';
import { toast } from 'sonner';
import { getCoverageDisplay } from '../../utils/coverageDisplay';

export default function ApplicantsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Phase 1 UX: deep-linkable — /manager/applicants?shiftId=<id> pre-selects
  // the exact shift the manager tapped from. Falls back to first-shift-in-list
  // when no param present (existing behaviour).
  const shiftIdParam = searchParams.get('shiftId');
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(shiftIdParam || null);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  // Phase 4 UX (Feb 2026): Confirm-before-assign dialog so managers see
  // exactly what will happen ("this worker takes the shift, others are
  // notified it's filled") before the action fires.
  const [confirmChoose, setConfirmChoose] = useState(null); // applicant object or null

  const fetchShifts = useCallback(async () => {
    try {
      // Fetch both open and assigned shifts that may have applicants
      const [openRes, assignedRes] = await Promise.all([
        api.get('/shifts?status=open'),
        api.get('/shifts?status=assigned')
      ]);
      // For assigned shifts, only include those with backup applicants
      const assignedWithBackups = assignedRes.data.filter(s => s.backupCount > 0 || s.applicantCount > 0);
      const combined = [...openRes.data, ...assignedWithBackups];
      setShifts(combined);
      // Only auto-select the first shift when no explicit deep-link was
      // supplied AND nothing is already selected.
      if (!shiftIdParam && combined.length > 0 && !selectedShift) {
        setSelectedShift(combined[0].id);
      }
    } catch (error) {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [api, selectedShift]);

  useEffect(() => {
    fetchShifts();
  }, []);

  const fetchApplicants = useCallback(async (shiftId) => {
    try {
      const response = await api.get(`/applications/shift/${shiftId}`);
      setApplicants(response.data);
    } catch (error) {
      console.error('Failed to load applicants:', error);
    }
  }, [api]);

  useEffect(() => {
    if (selectedShift) {
      fetchApplicants(selectedShift);
    }
  }, [selectedShift, fetchApplicants]);

  const openWorkerProfile = async (workerId) => {
    setSelectedWorkerId(workerId);
    setProfileLoading(true);
    try {
      const res = await api.get(`/staff/worker/${workerId}/profile`);
      setProfileData(res.data);
    } catch {
      toast.error('Failed to load worker profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleVerifyFromProfile = async (workerId) => {
    setActionLoading('verify-' + workerId);
    try {
      await api.patch(`/users/${workerId}/verify-smart-id`);
      toast.success('Verification updated');
      const res = await api.get(`/staff/worker/${workerId}/profile`);
      setProfileData(res.data);
      if (selectedShift) fetchApplicants(selectedShift);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update verification');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAccept = async (applicationId) => {
    setActionLoading(applicationId);
    try {
      await api.post(`/applications/${applicationId}/accept`);
      toast.success('Worker assigned to shift! Other applicants kept as backups.');
      try {
        const planRes = await api.get('/payments/my-plan');
        if (planRes.data.planType === 'payg' || planRes.data.planType === 'none' || !planRes.data.planType) {
          toast.info('Go to My Shifts to complete the shift payment.', { duration: 5000 });
        }
      } catch { /* non-blocking billing hint */ }
      fetchShifts();
      if (selectedShift) fetchApplicants(selectedShift);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept application');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (applicationId) => {
    setActionLoading(applicationId);
    try {
      await api.post(`/applications/${applicationId}/reject`);
      toast.success('Application rejected');
      fetchApplicants(selectedShift);
    } catch (error) {
      toast.error('Failed to reject application');
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmSmartId = async (workerId) => {
    setActionLoading('confirm-' + workerId);
    try {
      await api.patch(`/users/${workerId}/confirm-smart-id`);
      toast.success('Smart ID confirmed with HR');
      fetchApplicants(selectedShift);
    } catch (e) {
      toast.error('Failed to confirm');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (str) => {
    if (!str) return '-';
    return new Date(str).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const currentShift = shifts.find(s => s.id === selectedShift);
  
  // Categorize applicants
  const assignedApplicant = applicants.find(a => a.applicationStatus === 'accepted');
  const pendingApplicants = applicants.filter(a => a.applicationStatus === 'pending');
  const backupApplicants = applicants.filter(a => a.applicationStatus === 'backup');
  const hasReviewableApplicants = pendingApplicants.length > 0 || backupApplicants.length > 0 || assignedApplicant;

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-14 w-full max-w-md" />
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map(i => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  const renderApplicantCard = (applicant, isBackup = false) => (
    <Card 
      key={applicant.id} 
      className={`border-0 shadow-sm hover:shadow-md transition-shadow ${isBackup ? 'ring-1 ring-amber-200' : ''}`}
      data-testid={`applicant-card-${applicant.id}`}
    >
      <CardContent className="p-5">
        {/* Status indicator */}
        {isBackup && (
          <div className="flex items-center gap-2 mb-3 px-3 py-1.5 bg-amber-50 rounded-lg" data-testid={`backup-status-${applicant.id}`}>
            <RefreshCw className="h-3.5 w-3.5 text-amber-600" />
            <span className="text-xs font-medium text-amber-700">Backup / Available for reassignment</span>
          </div>
        )}
        
        {/* Applicant Header */}
        <div className="flex items-start gap-4 mb-3">
          <WorkerAvatar
            profilePhoto={applicant.workerPhoto}
            name={applicant.workerName}
            size={56}
            testId={`applicant-avatar-${applicant.id}`}
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 text-lg">{applicant.workerName}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{applicant.workerEmail}</p>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              {applicant.trustStatus && (
                <Badge
                  className={`gap-1.5 text-xs font-semibold border ${
                    applicant.trustStatus.level === 'trusted' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                    applicant.trustStatus.level === 'use_caution' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                    'bg-red-100 text-red-800 border-red-200'
                  }`}
                  data-testid={`trust-badge-${applicant.id}`}
                >
                  <span className={`h-2 w-2 rounded-full ${
                    applicant.trustStatus.level === 'trusted' ? 'bg-emerald-500' :
                    applicant.trustStatus.level === 'use_caution' ? 'bg-amber-500' : 'bg-red-500'
                  }`} />
                  {applicant.trustStatus.label}
                </Badge>
              )}
              {applicant.smartIdManagerConfirmed ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="inline-flex" data-testid={`verified-badge-${applicant.id}`}>
                      <Badge className="bg-emerald-100 text-emerald-800 gap-1 cursor-pointer hover:opacity-80 transition-opacity">
                        <ShieldCheck className="h-3 w-3" /> Smart ID Verified
                        <Info className="h-2.5 w-2.5 ml-0.5 opacity-60" />
                      </Badge>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3" side="bottom" align="start">
                    <h4 className="text-sm font-semibold text-slate-900 mb-2">Verification Details</h4>
                    <div className="space-y-1.5 text-sm">
                      {applicant.smartIdConfirmedBranch && (
                        <p className="text-slate-600"><span className="text-slate-400">Branch:</span> {applicant.smartIdConfirmedBranch}</p>
                      )}
                      {applicant.smartIdConfirmerName && (
                        <p className="text-slate-600"><span className="text-slate-400">Verified by:</span> {applicant.smartIdConfirmerName}</p>
                      )}
                      {applicant.smartIdConfirmedAt && (
                        <p className="text-slate-600"><span className="text-slate-400">Date:</span> {formatDate(applicant.smartIdConfirmedAt)}</p>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              ) : applicant.smartIdSelfDeclared || applicant.smartIdVerified ? (
                <Badge className="bg-amber-100 text-amber-800 gap-1">
                  <ShieldCheck className="h-3 w-3" /> Self-Declared
                </Badge>
              ) : (
                <Badge className="bg-red-100 text-red-800 gap-1">
                  <XCircle className="h-3 w-3" /> Not Verified
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* HR Check Prompt */}
        <div className="flex items-start gap-2 p-2.5 bg-amber-50 rounded-lg mb-3" data-testid={`hr-prompt-${applicant.id}`}>
          <Phone className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-amber-700 font-medium">Please call HR to confirm Smart ID before approving.</p>
            {applicant.smartIdManagerConfirmed && (
              <p className="text-xs text-amber-600 mt-0.5" data-testid={`hr-previously-verified-${applicant.id}`}>
                Previously verified{applicant.smartIdConfirmedBranch ? ` by ${applicant.smartIdConfirmedBranch}` : ''}{applicant.smartIdConfirmerName ? ` (${applicant.smartIdConfirmerName})` : ''}, but HR confirmation is still required before approval.
              </p>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-2 mb-3 py-2.5 px-2 bg-slate-50 rounded-xl">
          <div className="text-center">
            <div className="flex items-center justify-center gap-0.5 text-amber-500 mb-0.5">
              <Star className="h-3.5 w-3.5 fill-current" />
              <span className="font-semibold text-sm">{applicant.ratingAverage.toFixed(1)}</span>
            </div>
            <p className="text-[10px] text-slate-500">Rating</p>
          </div>
          <div className="text-center">
            <p className={`font-semibold text-sm ${
              applicant.reliabilityScore >= 85 ? 'text-emerald-600' : applicant.reliabilityScore >= 60 ? 'text-amber-600' : 'text-red-600'
            }`}>{applicant.reliabilityScore}%</p>
            <p className="text-[10px] text-slate-500">Reliable</p>
          </div>
          <div className="text-center">
            <p className="font-semibold text-sm text-slate-900">{applicant.completedShiftsCount}</p>
            <p className="text-[10px] text-slate-500">Shifts</p>
          </div>
          <div className="text-center">
            <p className={`font-semibold text-sm ${applicant.cancellationCount > 0 ? 'text-amber-600' : 'text-slate-900'}`}>
              {applicant.cancellationCount || 0}
            </p>
            <p className="text-[10px] text-slate-500">Cancels</p>
          </div>
          <div className="text-center">
            <p className={`font-semibold text-sm ${applicant.noShowCount > 0 ? 'text-red-600' : 'text-slate-900'}`}>
              {applicant.noShowCount || 0}
            </p>
            <p className="text-[10px] text-slate-500">No-shows</p>
          </div>
        </div>

        {/* Risk indicator */}
        {(applicant.noShowCount > 0 || applicant.cancellationCount >= 3 || applicant.reliabilityScore < 60) && (
          <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg mb-3" data-testid={`risk-warning-${applicant.id}`}>
            <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
            <p className="text-xs text-red-600">
              {applicant.noShowCount > 0 && `${applicant.noShowCount} no-show${applicant.noShowCount > 1 ? 's' : ''}`}
              {applicant.noShowCount > 0 && applicant.cancellationCount >= 3 && ' + '}
              {applicant.cancellationCount >= 3 && `${applicant.cancellationCount} cancellations`}
              {applicant.reliabilityScore < 60 && ` - Low reliability (${applicant.reliabilityScore}%)`}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2 pt-3 border-t border-slate-100">
          <div className="flex gap-2">
            {/* Show Assign/Reassign button for pending and backup applicants (not for already-accepted) */}
            {(applicant.applicationStatus === 'pending' || applicant.applicationStatus === 'backup') && (
              <>
                <Button
                  className="flex-1 h-11 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white font-medium"
                  onClick={() => setConfirmChoose(applicant)}
                  disabled={actionLoading === applicant.id}
                  data-testid={`accept-btn-${applicant.id}`}
                >
                  {actionLoading === applicant.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <><CheckCircle2 className="h-4 w-4 mr-1.5" /> {isBackup ? 'Reassign' : 'Choose worker'}</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-11 rounded-full border-2 border-slate-200 text-slate-600 font-medium hover:bg-slate-50"
                  onClick={() => handleReject(applicant.id)}
                  disabled={actionLoading === applicant.id}
                  data-testid={`reject-btn-${applicant.id}`}
                >
                  <XCircle className="h-4 w-4 mr-1.5" /> Reject
                </Button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1 h-9 rounded-full text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={() => openWorkerProfile(applicant.workerId)}
              data-testid={`view-profile-btn-${applicant.id}`}
            >
              <Eye className="h-3.5 w-3.5 mr-1" /> View profile
            </Button>
            <Button
              variant="outline"
              className="flex-1 h-9 rounded-full text-xs"
              onClick={() => navigate(`/manager/chat?shiftId=${applicant.shiftId}&workerId=${applicant.workerId}`)}
              data-testid={`message-btn-${applicant.id}`}
            >
              <MessageSquare className="h-3.5 w-3.5 mr-1" /> Message
            </Button>
            {!applicant.smartIdManagerConfirmed && (applicant.smartIdSelfDeclared || applicant.smartIdVerified) && (
              <Button
                variant="outline"
                className="flex-1 h-9 rounded-full text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                onClick={() => handleConfirmSmartId(applicant.workerId)}
                disabled={actionLoading === 'confirm-' + applicant.workerId}
                data-testid={`confirm-smartid-btn-${applicant.id}`}
              >
                {actionLoading === 'confirm-' + applicant.workerId ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                ) : (
                  <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                )}
                Confirm Smart ID
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 space-y-6" data-testid="applicants-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']">Available workers</h1>
        <p className="text-slate-500 mt-1">Choose who takes this shift.</p>
      </div>

      {shifts.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Shifts with Applicants</h3>
            <p className="text-slate-500">Create a shift to start receiving applications</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Shift Selector */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <Select value={selectedShift || ''} onValueChange={setSelectedShift}>
                <SelectTrigger className="h-14 rounded-xl" data-testid="shift-selector">
                  <SelectValue placeholder="Select a shift" />
                </SelectTrigger>
                <SelectContent>
                  {shifts.map(shift => (
                    <SelectItem key={shift.id} value={shift.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{shift.branchName}</span>
                        <span className="text-slate-500">- {shift.date}</span>
                        <Badge className={shift.status === 'assigned' ? 'bg-amber-100 text-amber-800 ml-2' : 'bg-blue-100 text-blue-800 ml-2'}>
                          {shift.status === 'assigned' ? `${shift.backupCount || 0} backup` : `${shift.applicantCount} applicants`}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Selected Shift Info */}
          {currentShift && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5">
                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span>{currentShift.location}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4 text-slate-400" />
                    <span>{currentShift.scheduledStartTime} - {currentShift.scheduledEndTime}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Banknote className="h-4 w-4 text-slate-400" />
                    <span>&pound;{currentShift.pay?.toFixed(2)}</span>
                  </div>
                  <Badge className={currentShift.status === 'assigned' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}>
                    {currentShift.status === 'assigned' ? 'Assigned' : 'Open'}
                  </Badge>
                  {(() => {
                    const cov = getCoverageDisplay(currentShift);
                    if (!cov) return null;
                    return (
                      <Badge className="bg-blue-100 text-blue-800" data-testid="applicant-coverage-badge">
                        {cov.full}
                      </Badge>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Assigned Worker Section */}
          {assignedApplicant && (
            <div className="space-y-3" data-testid="assigned-section">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-emerald-500"></div>
                <h2 className="text-base font-semibold text-slate-900">Assigned Worker</h2>
              </div>
              <Card className="border-0 shadow-sm ring-1 ring-emerald-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <WorkerAvatar
                      profilePhoto={assignedApplicant.workerPhoto}
                      name={assignedApplicant.workerName}
                      size={56}
                      ringClass="ring-2 ring-emerald-500"
                      testId="assigned-worker-avatar"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 text-lg">{assignedApplicant.workerName}</h3>
                      <p className="text-xs text-slate-400">{assignedApplicant.workerEmail}</p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-800 font-semibold" data-testid="assigned-badge">
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Assigned
                    </Badge>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      variant="outline"
                      className="h-9 rounded-full text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                      onClick={() => openWorkerProfile(assignedApplicant.workerId)}
                      data-testid={`view-profile-btn-assigned`}
                    >
                      <Eye className="h-3.5 w-3.5 mr-1" /> View Profile
                    </Button>
                    <Button
                      variant="outline"
                      className="h-9 rounded-full text-xs"
                      onClick={() => navigate(`/manager/chat?shiftId=${assignedApplicant.shiftId}&workerId=${assignedApplicant.workerId}`)}
                      data-testid={`message-btn-assigned`}
                    >
                      <MessageSquare className="h-3.5 w-3.5 mr-1" /> Message
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Backup Applicants Section */}
          {backupApplicants.length > 0 && (
            <div className="space-y-3" data-testid="backup-section">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                <h2 className="text-base font-semibold text-slate-900">Backup Applicants</h2>
                <Badge className="bg-amber-100 text-amber-800">{backupApplicants.length}</Badge>
              </div>
              <p className="text-xs text-slate-500 -mt-1">These applicants remain available until the assigned worker starts the shift. You can reassign if needed.</p>
              <div className="grid gap-4 md:grid-cols-2">
                {backupApplicants.map(applicant => renderApplicantCard(applicant, true))}
              </div>
            </div>
          )}

          {/* iter98 — Best-fit recommendation. Rendered above the Pending
              list so the manager sees the single highest-scoring applicant
              first, with a Why this worker? explanation and "View all
              applicants" implicit via the section below. Only shown while
              there are pending applicants (no need for a recommendation
              when the shift is already assigned). */}
          {pendingApplicants.length > 0 && selectedShift && currentShift?.status !== 'assigned' && (
            <BestMatchCard
              shiftId={selectedShift}
              onChoose={(top) => {
                const match = pendingApplicants.find(a => a.workerId === top.workerId);
                if (match) setConfirmChoose(match);
              }}
            />
          )}

          {/* Pending Applicants Section */}
          {pendingApplicants.length > 0 && (
            <div className="space-y-3" data-testid="pending-section">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                <h2 className="text-base font-semibold text-slate-900" data-testid="pending-applicants-heading">View all applicants</h2>
                <Badge className="bg-blue-100 text-blue-800">{pendingApplicants.length}</Badge>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {pendingApplicants.map(applicant => renderApplicantCard(applicant, false))}
              </div>
            </div>
          )}

          {/* No reviewable applicants */}
          {!hasReviewableApplicants && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Active Applicants</h3>
                <p className="text-slate-500">No one has applied to this shift yet</p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Worker Profile Modal */}
      <WorkerProfileModal
        open={!!selectedWorkerId}
        onClose={() => { setSelectedWorkerId(null); setProfileData(null); }}
        profileData={profileData}
        profileLoading={profileLoading}
        onVerify={handleVerifyFromProfile}
        actionLoading={actionLoading}
        showStaffActions={false}
      />

      {/* Phase 4 UX: Confirm before assigning so the manager knows exactly
          what happens next — the chosen worker gets the shift and other
          applicants are told it's been filled. */}
      <Dialog open={!!confirmChoose} onOpenChange={(open) => { if (!open) setConfirmChoose(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-choose-dialog">
          <DialogHeader>
            <DialogTitle data-testid="confirm-choose-title">
              {confirmChoose ? `Confirm ${confirmChoose.workerName} for this shift?` : 'Confirm worker'}
            </DialogTitle>
            <DialogDescription data-testid="confirm-choose-description">
              The selected worker will get this shift. Other applicants will be told the shift has been filled.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              className="rounded-full min-h-[48px]"
              onClick={() => setConfirmChoose(null)}
              data-testid="confirm-choose-cancel-btn"
            >
              Not yet
            </Button>
            <Button
              className="rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white min-h-[48px]"
              onClick={async () => {
                if (!confirmChoose) return;
                const id = confirmChoose.id;
                setConfirmChoose(null);
                await handleAccept(id);
              }}
              disabled={actionLoading != null}
              data-testid="confirm-choose-submit-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><CheckCircle2 className="h-4 w-4 mr-1.5" /> Confirm worker</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
